import React, { useMemo, useState } from "react";

import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { useController, useFormContext } from "react-hook-form";

import { Button } from "@/src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/src/components/ui/command";
import { Field, FieldError, FieldLabel } from "@/src/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { cn } from "@/src/lib/utils";
import { groupComboboxOptionList } from "@/src/utils/comboboxOptions";

import type { JSX, ReactNode } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

import type { ComboboxOption } from "./MultiCombobox";

const SCROLL_THRESHOLD_PX = 24;

interface ComboboxBaseProps {
  label?: string;
  value?: string | null;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  disabled?: boolean;
  error?: string;
  options: ComboboxOption[];
  popoverContainer?: HTMLElement | null;
  popoverClassName?: string;
  className?: string;
  onValueChange: (value: string) => void;
  onSearchChange?: (search: string) => void;
  onLoadMore?: () => void;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  loadingLabel?: string;
  selectedLabel?: string | null;
  onClear?: () => void;
  clearLabel?: string;
  renderOption?: (option: ComboboxOption) => ReactNode;
}

export function ComboboxBase({
  label,
  value,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  disabled,
  error,
  options,
  popoverContainer,
  popoverClassName,
  className,
  onValueChange,
  onSearchChange,
  onLoadMore,
  isLoading,
  isFetchingMore,
  loadingLabel,
  selectedLabel,
  onClear,
  clearLabel,
  renderOption,
}: ComboboxBaseProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const isControlledSearch = Boolean(onSearchChange);

  const selectedOption = options.find((option) => option.value === value);
  const displayLabel = selectedLabel ?? selectedOption?.label;
  const TriggerIcon = isLoading ? Loader2 : ChevronsUpDown;
  const showClear = Boolean(onClear) && Boolean(displayLabel) && !disabled;

  const showStatusMessage =
    isLoading || (isControlledSearch && options.length === 0);

  const optionGroupList = useMemo(
    () => groupComboboxOptionList(options),
    [options],
  );

  const handleSelect = (optionValue: string): void => {
    onValueChange(optionValue);
    setOpen(false);
  };

  const handleSearchChange = (next: string): void => {
    setSearch(next);
    onSearchChange?.(next);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const scrollElement = event.currentTarget;
    const reachedBottom =
      scrollElement.scrollHeight -
        scrollElement.scrollTop -
        scrollElement.clientHeight <
      SCROLL_THRESHOLD_PX;
    if (reachedBottom) {
      onLoadMore?.();
    }
  };

  return (
    <Field className={className}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <div className="relative">
        <Popover onOpenChange={setOpen} open={open}>
          <PopoverTrigger asChild>
            <Button
              aria-expanded={open}
              aria-invalid={error ? true : undefined}
              disabled={disabled}
              role="combobox"
              variant="outline"
              className={cn(
                "bg-card w-full justify-between font-normal",
                showClear && "pr-9",
              )}
            >
              <span
                className={cn(
                  "truncate",
                  !displayLabel && "text-muted-foreground",
                )}
              >
                {displayLabel ??
                  (isLoading && value ? loadingLabel : placeholder)}
              </span>
              {!showClear ? (
                <TriggerIcon
                  className={cn(
                    "ml-2 size-4 shrink-0 opacity-50",
                    isLoading && "animate-spin",
                  )}
                />
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            container={popoverContainer}
            className={cn(
              "w-(--radix-popover-trigger-width) p-0",
              popoverClassName,
            )}
          >
            <Command shouldFilter={!isControlledSearch}>
              <CommandInput
                placeholder={searchPlaceholder}
                value={isControlledSearch ? search : undefined}
                onValueChange={
                  isControlledSearch ? handleSearchChange : undefined
                }
              />
              <CommandList onScroll={handleScroll}>
                {showStatusMessage ? (
                  <div className="text-muted-foreground py-6 text-center text-sm">
                    {isLoading ? loadingLabel : emptyLabel}
                  </div>
                ) : null}
                {!showStatusMessage && !isControlledSearch ? (
                  <CommandEmpty>{emptyLabel}</CommandEmpty>
                ) : null}
                {optionGroupList.map((group, groupIndex) => (
                  <React.Fragment key={group.heading ?? "ungrouped"}>
                    {groupIndex > 0 ? <CommandSeparator /> : null}
                    <CommandGroup heading={group.heading}>
                      {group.options.map((option) => (
                        <CommandItem
                          key={option.value}
                          onSelect={(): void => handleSelect(option.value)}
                          value={`${option.label} ${option.value}`}
                        >
                          <Check
                            className={cn(
                              "mr-2 size-4",
                              option.value === value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          {renderOption ? renderOption(option) : option.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                ))}
                {isFetchingMore ? (
                  <div className="text-muted-foreground py-2 text-center text-sm">
                    {loadingLabel}
                  </div>
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {showClear ? (
          <button
            aria-label={clearLabel}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
            onClick={onClear}
            type="button"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>
      <FieldError>{error}</FieldError>
    </Field>
  );
}

interface ComboboxProps<T extends FieldValues> {
  name: FieldPathByValue<T, string | null | undefined>;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  options: ComboboxOption[];
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  popoverContainer?: HTMLElement | null;
  popoverClassName?: string;
  renderOption?: (option: ComboboxOption) => ReactNode;
}

/**
 * Searchable `Combobox` bound to a react-hook-form string field via
 * `useController`. The validation error is read from the field state.
 */
export function Combobox<T extends FieldValues>({
  name,
  label,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  options,
  disabled,
  isLoading,
  loadingLabel,
  popoverContainer,
  popoverClassName,
  renderOption,
}: ComboboxProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  return (
    <ComboboxBase
      disabled={disabled}
      emptyLabel={emptyLabel}
      error={fieldState.error?.message}
      isLoading={isLoading}
      label={label}
      loadingLabel={loadingLabel}
      onValueChange={field.onChange}
      options={options}
      placeholder={placeholder}
      popoverClassName={popoverClassName}
      popoverContainer={popoverContainer}
      renderOption={renderOption}
      searchPlaceholder={searchPlaceholder}
      value={field.value}
    />
  );
}
